import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { previewString } from './TemplateEditor';
import Button from '../Button';

function getAxiosConfig() {
  return { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
}

/** Sort by last_used_at desc, nulls last. */
function byRecentlyUsed(a, b) {
  const at = a.last_used_at ? new Date(a.last_used_at).getTime() : -Infinity;
  const bt = b.last_used_at ? new Date(b.last_used_at).getTime() : -Infinity;
  return bt - at;
}

/**
 * TemplatesRail — horizontal rail of the 3 most-recently-used templates.
 *
 * Props:
 *  - onInsert:   (template) => void
 *  - onSeeAll:   () => void   opens the full library
 *  - onNew:      () => void   opens a blank editor
 *  - reloadKey:  any          change to force a refetch (e.g. after a save/delete)
 */
export default function TemplatesRail({ onInsert, onSeeAll, onNew, reloadKey }) {
  const { API_URL } = useAuth();
  const [templates, setTemplates] = useState([]);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/coach-templates/`, getAxiosConfig());
      setTemplates(res.data.templates || []);
    } catch (err) {
      console.error('Error loading templates:', err);
    }
  }, [API_URL]);

  useEffect(() => { load(); }, [load, reloadKey]);

  const top3 = [...templates].sort(byRecentlyUsed).slice(0, 3);

  return (
    <div
      className="templates-rail"
      style={{
        display: 'flex', alignItems: 'stretch', gap: '8px', padding: '8px 12px',
        overflowX: 'auto', borderTop: '1px solid #f1f5f9', background: '#fafafa',
      }}
    >
      {top3.map((t) => (
        <button
          key={t.id}
          type="button"
          className="template-rail-card"
          onClick={() => onInsert(t)}
          title={`Insert "${t.name}"`}
          style={{
            flex: '0 0 auto', maxWidth: '180px', textAlign: 'left', padding: '8px 10px',
            borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff',
            cursor: 'pointer', display: 'block',
          }}
        >
          <div style={{
            display: 'block', fontSize: '13px', fontWeight: 600, color: '#1e293b',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {t.name}
          </div>
          <div style={{
            display: 'block', fontSize: '11px', color: '#64748b', whiteSpace: 'nowrap',
            overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '160px',
          }}>
            {previewString(t.parts) || '—'}
          </div>
        </button>
      ))}

      <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: '6px', marginLeft: top3.length ? '4px' : 0 }}>
        <Button
          type="button"
          variant="secondary"
          className="template-rail-action"
          onClick={onSeeAll}
          style={{
            padding: '6px 10px', border: '1px solid #e2e8f0',
            background: '#fff', color: '#4338ca', fontSize: '12px', whiteSpace: 'nowrap',
          }}
        >
          See all
        </Button>
        <Button
          type="button"
          variant="secondary"
          className="template-rail-action"
          onClick={onNew}
          style={{
            padding: '6px 10px', border: '1px solid #c7d2fe',
            background: '#eef2ff', color: '#4338ca', fontSize: '12px', whiteSpace: 'nowrap',
          }}
        >
          + New
        </Button>
      </div>
    </div>
  );
}
